/**
 * Mobile Client360 — dark, deeply editable CRM for /app
 *
 * Two views:
 *   1. ClientListPage — searchable list of all clients
 *   2. ClientDetailPage — full profile with inline auto-save on blur
 *
 * Editable fields (PATCH /api/command-center/clients/360/{client_key}):
 *   name, email, phone, tier, company, notes, tags, custom_fields[],
 *   preferences{drink,music,allergies,seating,custom[]}, manual_lifetime_value,
 *   last_contact_date
 *
 * Auth: relies on the global fetch interceptor (Bearer header from /m unlock).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const T = {
  black: '#FFFFFF', dark: '#FAFAFA', panel: '#FFFFFF', card: '#FFFFFF',
  border: '#E5E5E5', red: '#CC0000', gold: '#A8862F', green: '#15803D',
  amber: '#B45309', white: '#0A0A0A', light: '#525252', dim: '#A3A3A3',
};

const fmt$ = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;
const TIERS = ['', 'lobby', 'condo', 'penthouse'];
const CF_TYPES = ['text', 'number', 'date', 'boolean'];

// ─── List Page ────────────────────────────────────────────────────────
export function MobileClientList({ onOpen }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [segment, setSegment] = useState('all');
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('search', q);
      if (segment !== 'all') params.set('segment', segment);
      const r = await fetch(`${API}/api/command-center/clients?${params}`);
      const d = await r.json();
      setClients(d.clients || []);
    } catch (e) { toast.error('Failed to load clients'); }
    setLoading(false);
  }, [q, segment]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const SEGMENTS = [
    { id: 'all', label: 'All' },
    { id: 'vip', label: 'VIP' },
    { id: 'active', label: 'Active' },
    { id: 'consulting', label: 'Consulting' },
    { id: 'corporate', label: 'Corporate' },
  ];

  return (
    <div data-testid="mobile-client-list">
      <p style={{ fontSize: 9, color: T.red, textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 700, margin: '0 4px 8px' }}>Clients</p>

      <input
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder="Search by name, phone, email, company…"
        style={{
          width: '100%', background: T.card, border: `1px solid ${T.border}`,
          padding: '12px 14px', color: T.white, fontSize: 13, outline: 'none',
          boxSizing: 'border-box',
        }}
        data-testid="client-list-search"
      />

      <div style={{ display: 'flex', gap: 6, marginTop: 10, overflowX: 'auto', paddingBottom: 4 }} data-testid="client-list-segments">
        {SEGMENTS.map(s => (
          <button
            key={s.id}
            onClick={() => setSegment(s.id)}
            style={{
              flexShrink: 0, padding: '6px 12px',
              background: segment === s.id ? T.red : T.card,
              color: segment === s.id ? T.white : T.light,
              border: `1px solid ${segment === s.id ? T.red : T.border}`,
              fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.1em', cursor: 'pointer',
            }}
            data-testid={`segment-${s.id}`}
          >{s.label}</button>
        ))}
      </div>

      <p style={{ fontSize: 10, color: T.dim, margin: '14px 4px 8px', fontVariantNumeric: 'tabular-nums' }}>
        {loading ? 'Loading…' : `${clients.length} client${clients.length === 1 ? '' : 's'}`}
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {clients.map((c) => (
          <button
            key={c.client_key}
            onClick={() => onOpen(c.client_id || c.client_key)}
            style={{
              background: T.card, border: `1px solid ${T.border}`,
              padding: 14, cursor: 'pointer', textAlign: 'left', color: T.white,
            }}
            data-testid={`client-row-${c.client_key}`}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <p style={{ fontSize: 14, fontWeight: 800, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name || 'Unknown'}</p>
                <p style={{ fontSize: 11, color: T.light, margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.phone || c.email || '—'}</p>
                {c.company && <p style={{ fontSize: 10, color: T.dim, margin: '2px 0 0' }}>{c.company}</p>}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                {c.tier && <span style={{ fontSize: 9, color: T.gold, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>♠ {c.tier}</span>}
                {c.total_spent > 0 && <p style={{ fontSize: 11, color: T.green, fontFamily: 'DM Mono, monospace', margin: '2px 0 0', fontWeight: 700 }}>{fmt$(c.total_spent)}</p>}
              </div>
            </div>
            {(c.tags || []).length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                {(c.tags || []).slice(0, 4).map(tg => (
                  <span key={tg} style={{ fontSize: 9, color: T.light, background: T.dark, padding: '2px 6px', borderRadius: 2 }}>{tg}</span>
                ))}
              </div>
            )}
          </button>
        ))}
        {!loading && clients.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: T.dim, fontSize: 12, border: `1px dashed ${T.border}` }}>
            No clients match.
          </div>
        )}
      </div>

      {/* Floating Action Button — Quick Add */}
      <button
        onClick={() => setShowAdd(true)}
        style={{
          position: 'fixed', right: 18, bottom: 90, zIndex: 50,
          width: 56, height: 56, borderRadius: '50%',
          background: T.red, color: '#FFFFFF', border: 0,
          fontSize: 28, fontWeight: 300, cursor: 'pointer',
          boxShadow: '0 4px 14px rgba(204,0,0,0.35), 0 2px 6px rgba(0,0,0,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          lineHeight: 1, paddingBottom: 3,
        }}
        aria-label="Quick Add Client"
        data-testid="client-fab-add"
      >+</button>

      {showAdd && (
        <QuickAddClientModal
          onClose={() => setShowAdd(false)}
          onCreated={(clientId) => { setShowAdd(false); onOpen(clientId); }}
        />
      )}
    </div>
  );
}

// ─── Quick Add Modal ──────────────────────────────────────────────────
function QuickAddClientModal({ onClose, onCreated }) {
  const [draft, setDraft] = useState({ name: '', phone: '', email: '', tier: '', company: '', notes: '' });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!draft.name.trim() && !draft.phone.trim() && !draft.email.trim()) {
      toast.error('Add at least a name, phone, or email');
      return;
    }
    setBusy(true);
    try {
      // Generate a key — use phone, email, or random — backend will dedupe
      const key = draft.phone.replace(/\D+/g, '').slice(-10)
               || draft.email.trim().toLowerCase()
               || `NEW-${Date.now().toString(36).toUpperCase()}`;
      const r = await fetch(`${API}/api/command-center/clients/360/${encodeURIComponent(key)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Create failed');
      const d = await r.json();
      toast.success(d.created ? 'Client created' : 'Client updated');
      onCreated(d.client_id);
    } catch (e) {
      toast.error(e.message);
    }
    setBusy(false);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
      data-testid="client-add-modal"
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 480,
          background: T.panel, borderTop: `2px solid ${T.red}`,
          padding: 20, paddingBottom: 28,
          animation: 'slideUp 0.2s ease-out',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <p style={{ fontSize: 9, color: T.red, textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 800, margin: 0 }}>Quick Add Client</p>
          <button onClick={onClose} style={{ background: 'none', border: 0, color: T.light, fontSize: 22, cursor: 'pointer', padding: 0, lineHeight: 1 }} data-testid="client-add-close">×</button>
        </div>
        <h2 style={{ fontSize: 18, fontWeight: 900, color: T.white, margin: '0 0 14px' }}>New Lead</h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input autoFocus value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} placeholder="Full name" style={inputStyle()} data-testid="client-add-name" />
          <input value={draft.phone} onChange={e => setDraft({ ...draft, phone: e.target.value })} placeholder="Phone" type="tel" inputMode="tel" style={inputStyle()} data-testid="client-add-phone" />
          <input value={draft.email} onChange={e => setDraft({ ...draft, email: e.target.value })} placeholder="Email" type="email" inputMode="email" style={inputStyle()} data-testid="client-add-email" />
          <input value={draft.company} onChange={e => setDraft({ ...draft, company: e.target.value })} placeholder="Company (optional)" style={inputStyle()} data-testid="client-add-company" />
          <select value={draft.tier} onChange={e => setDraft({ ...draft, tier: e.target.value })} style={inputStyle({ color: T.white })} data-testid="client-add-tier">
            <option value="" style={{ background: T.dark }}>— No tier —</option>
            <option value="lobby" style={{ background: T.dark }}>Lobby</option>
            <option value="condo" style={{ background: T.dark }}>Condo</option>
            <option value="penthouse" style={{ background: T.dark }}>Penthouse</option>
          </select>
          <textarea value={draft.notes} onChange={e => setDraft({ ...draft, notes: e.target.value })} placeholder="Quick note (how you met, what they need…)" rows={3} style={inputStyle({ resize: 'vertical' })} data-testid="client-add-notes" />
        </div>

        <div style={{ display: 'flex', gap: 6, marginTop: 14 }}>
          <button
            onClick={onClose}
            style={{ flex: 1, background: 'transparent', border: `1px solid ${T.border}`, color: T.light, padding: '12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.15em', cursor: 'pointer' }}
            data-testid="client-add-cancel"
          >Cancel</button>
          <button
            onClick={submit}
            disabled={busy}
            style={{ flex: 2, background: T.red, color: '#FFFFFF', border: 0, padding: '12px', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.15em', cursor: 'pointer', opacity: busy ? 0.5 : 1 }}
            data-testid="client-add-save"
          >{busy ? 'Saving…' : 'Save & Open'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Detail Page ──────────────────────────────────────────────────────
export function MobileClientDetail({ clientKey, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({});
  const [tab, setTab] = useState('profile');

  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/command-center/clients/360/${encodeURIComponent(clientKey)}`);
        if (!r.ok) throw new Error('Not found');
        const d = await r.json();
        if (cancelled) return;
        setData(d);
        const p = d.profile || {};
        setDraft({
          name: p.name || '', email: p.email || '', phone: p.phone || '',
          company: p.company || '', tier: p.tier || '', notes: p.notes || '',
          tags: p.tags || [],
          custom_fields: p.custom_fields || [],
          preferences: {
            drink: p.preferences?.drink || '', music: p.preferences?.music || '',
            allergies: p.preferences?.allergies || '', seating: p.preferences?.seating || '',
            custom: p.preferences?.custom || [],
          },
          manual_lifetime_value: p.manual_lifetime_value,
          last_contact_date: p.last_contact_date || '',
        });
      } catch (e) { toast.error(e.message); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [clientKey, reloadTick]);

  // Save fires on blur. The draft state already reflects the user's edit
  // optimistically, so we only POST and toast — no setState here.
  const save = async (patch) => {
    try {
      const target = data?.profile?.client_id || clientKey;
      const r = await fetch(`${API}/api/command-center/clients/360/${encodeURIComponent(target)}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Save failed');
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
      setReloadTick(t => t + 1); // revert by reloading from server
    }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: T.dim, fontSize: 12 }} data-testid="client-detail-loading">Loading…</div>;
  }
  if (!data) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <p style={{ color: T.dim, fontSize: 13 }}>Client not found.</p>
        <button onClick={onBack} style={{ marginTop: 12, background: T.red, color: '#FFFFFF', border: 0, padding: '8px 16px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>← Back</button>
      </div>
    );
  }

  const p = data.profile || {};
  const s = data.stats || {};

  return (
    <div data-testid="mobile-client-detail">
      <button onClick={onBack} style={{ background: 'none', border: 0, color: T.red, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer', padding: '0 0 12px' }} data-testid="client-detail-back">← Clients</button>

      {/* Header */}
      <section style={{ background: T.panel, border: `1px solid ${T.border}`, padding: 16, marginBottom: 12 }}>
        <p style={{ fontSize: 9, color: T.red, textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 700, margin: 0 }}>Client 360°</p>
        <EditableText value={draft.name} onChange={v => setDraft(d => ({ ...d, name: v }))} onCommit={v => save({ name: v })} placeholder="Full Name" style={{ fontSize: 22, fontWeight: 900 }} testid="field-name" />
        <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
          {(p.sources || []).map(src => (
            <span key={src} style={{ fontSize: 9, color: T.light, background: T.dark, padding: '2px 6px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{src}</span>
          ))}
          {p.tier && <span style={{ fontSize: 9, color: T.gold, background: 'rgba(201,168,76,0.15)', padding: '2px 6px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>♠ {p.tier}</span>}
        </div>
      </section>

      {/* Stat tiles */}
      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4, marginBottom: 12 }} data-testid="client-detail-stats">
        <Stat label="Bookings" value={s.total_bookings || 0} />
        <Stat label="Upcoming" value={s.upcoming_bookings || 0} color={s.upcoming_bookings > 0 ? T.green : T.white} />
        <Stat label="LTV" value={fmt$(s.lifetime_value || 0)} color={T.gold} />
        <Stat label="Balance" value={fmt$(s.balance_due || 0)} color={s.balance_due > 0 ? T.red : T.white} />
      </section>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, borderBottom: `1px solid ${T.border}` }} data-testid="client-detail-tabs">
        {['profile', 'prefs', 'custom', 'history'].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              background: 'none', border: 0, color: tab === t ? T.red : T.light,
              borderBottom: `2px solid ${tab === t ? T.red : 'transparent'}`,
              padding: '8px 10px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase',
              letterSpacing: '0.1em', cursor: 'pointer',
            }}
            data-testid={`tab-${t}`}
          >{t === 'prefs' ? 'Preferences' : t === 'custom' ? 'Custom Fields' : t}</button>
        ))}
      </div>

      {tab === 'profile' && (
        <ProfileTab draft={draft} setDraft={setDraft} save={save} stats={s} />
      )}
      {tab === 'prefs' && (
        <PreferencesTab draft={draft} setDraft={setDraft} save={save} />
      )}
      {tab === 'custom' && (
        <CustomFieldsTab draft={draft} setDraft={setDraft} save={save} />
      )}
      {tab === 'history' && (
        <HistoryTab data={data} />
      )}

      {/* Danger zone — soft delete */}
      <section style={{ marginTop: 24, padding: 12, border: `1px solid ${T.border}` }} data-testid="client-detail-danger">
        <p style={{ fontSize: 9, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.2em', fontWeight: 700, margin: '0 0 6px' }}>Danger Zone</p>
        <button
          onClick={async () => {
            if (!window.confirm(`Delete ${p.name || 'this client'}? They can be restored from the archive.`)) return;
            try {
              const target = p.client_id || clientKey;
              const r = await fetch(`${API}/api/command-center/clients/360/${encodeURIComponent(target)}`, { method: 'DELETE' });
              if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Delete failed');
              toast.success('Client deleted');
              onBack();
            } catch (e) { toast.error(e.message); }
          }}
          style={{
            width: '100%', background: 'transparent', border: `1px solid ${T.red}`,
            color: T.red, padding: '12px', fontSize: 11, fontWeight: 800,
            textTransform: 'uppercase', letterSpacing: '0.15em', cursor: 'pointer',
          }}
          data-testid="client-detail-delete"
        >Delete Client</button>
      </section>
    </div>
  );
}

// ─── Tab: Profile ─────────────────────────────────────────────────────
const ProfileTab = ({ draft, setDraft, save, stats }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="profile-tab">
    <FieldRow label="Email">
      <EditableText value={draft.email} onChange={v => setDraft(d => ({ ...d, email: v }))} onCommit={v => save({ email: v })} placeholder="email@example.com" testid="field-email" />
    </FieldRow>
    <FieldRow label="Phone">
      <EditableText value={draft.phone} onChange={v => setDraft(d => ({ ...d, phone: v }))} onCommit={v => save({ phone: v })} placeholder="(555) 123-4567" testid="field-phone" />
    </FieldRow>
    <FieldRow label="Company">
      <EditableText value={draft.company} onChange={v => setDraft(d => ({ ...d, company: v }))} onCommit={v => save({ company: v })} placeholder="Company" testid="field-company" />
    </FieldRow>
    <FieldRow label="Tier">
      <SelectBox value={draft.tier} onChange={(v) => { setDraft(d => ({ ...d, tier: v })); save({ tier: v }); }} options={TIERS} testid="field-tier" />
    </FieldRow>
    <FieldRow label="Last Contact">
      <EditableText type="date" value={draft.last_contact_date} onChange={v => setDraft(d => ({ ...d, last_contact_date: v }))} onCommit={v => save({ last_contact_date: v })} testid="field-last-contact" />
    </FieldRow>
    <FieldRow label={`Lifetime Value — auto: ${fmt$(stats?.auto_lifetime_value || 0)}`}>
      <EditableText
        type="number"
        value={draft.manual_lifetime_value ?? ''}
        onChange={v => setDraft(d => ({ ...d, manual_lifetime_value: v }))}
        onCommit={(v) => save({ manual_lifetime_value: v === '' || v === null ? null : Number(v) })}
        placeholder="Manual override (blank = auto)"
        testid="field-manual-ltv"
      />
    </FieldRow>
    <FieldRow label="Tags">
      <TagEditor tags={draft.tags || []} onChange={(tags) => { setDraft(d => ({ ...d, tags })); save({ tags }); }} testid="field-tags" />
    </FieldRow>
    <FieldRow label="Notes">
      <EditableTextarea value={draft.notes} onChange={v => setDraft(d => ({ ...d, notes: v }))} onCommit={v => save({ notes: v })} placeholder="Internal notes…" rows={4} testid="field-notes" />
    </FieldRow>
  </div>
);

// ─── Tab: Preferences ─────────────────────────────────────────────────
const PreferencesTab = ({ draft, setDraft, save }) => {
  const updatePrefs = (patch) => {
    const next = { ...draft.preferences, ...patch };
    setDraft(d => ({ ...d, preferences: next }));
    save({ preferences: next });
  };
  const updateCustomPref = (idx, key, value) => {
    const next = [...(draft.preferences.custom || [])];
    next[idx] = { ...next[idx], [key]: value };
    const prefs = { ...draft.preferences, custom: next };
    setDraft(d => ({ ...d, preferences: prefs }));
    save({ preferences: prefs });
  };
  const addCustomPref = () => {
    const next = [...(draft.preferences.custom || []), { key: '', value: '' }];
    setDraft(d => ({ ...d, preferences: { ...d.preferences, custom: next } }));
  };
  const removeCustomPref = (idx) => {
    const next = (draft.preferences.custom || []).filter((_, i) => i !== idx);
    const prefs = { ...draft.preferences, custom: next };
    setDraft(d => ({ ...d, preferences: prefs }));
    save({ preferences: prefs });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="prefs-tab">
      <p style={{ fontSize: 9, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.2em', margin: 0 }}>Core</p>
      <FieldRow label="Drink">
        <EditableText value={draft.preferences.drink} onChange={v => setDraft(d => ({ ...d, preferences: { ...d.preferences, drink: v } }))} onCommit={v => updatePrefs({ drink: v })} placeholder="Espresso, Topo Chico…" testid="pref-drink" />
      </FieldRow>
      <FieldRow label="Music">
        <EditableText value={draft.preferences.music} onChange={v => setDraft(d => ({ ...d, preferences: { ...d.preferences, music: v } }))} onCommit={v => updatePrefs({ music: v })} placeholder="Jazz, Hip-hop…" testid="pref-music" />
      </FieldRow>
      <FieldRow label="Allergies / Dietary">
        <EditableText value={draft.preferences.allergies} onChange={v => setDraft(d => ({ ...d, preferences: { ...d.preferences, allergies: v } }))} onCommit={v => updatePrefs({ allergies: v })} placeholder="None / nut allergy / etc." testid="pref-allergies" />
      </FieldRow>
      <FieldRow label="Seating">
        <EditableText value={draft.preferences.seating} onChange={v => setDraft(d => ({ ...d, preferences: { ...d.preferences, seating: v } }))} onCommit={v => updatePrefs({ seating: v })} placeholder="Rear-right, Front…" testid="pref-seating" />
      </FieldRow>

      <p style={{ fontSize: 9, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.2em', margin: '12px 0 0' }}>Custom Preferences</p>
      {(draft.preferences.custom || []).map((cp, idx) => (
        <div key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center' }} data-testid={`pref-custom-${idx}`}>
          <input
            value={cp.key || ''}
            onChange={e => updateCustomPref(idx, 'key', e.target.value)}
            onBlur={() => save({ preferences: draft.preferences })}
            placeholder="Label"
            style={inputStyle({ width: 110 })}
            data-testid={`pref-custom-key-${idx}`}
          />
          <input
            value={cp.value || ''}
            onChange={e => updateCustomPref(idx, 'value', e.target.value)}
            onBlur={() => save({ preferences: draft.preferences })}
            placeholder="Value"
            style={inputStyle({ flex: 1 })}
            data-testid={`pref-custom-value-${idx}`}
          />
          <button onClick={() => removeCustomPref(idx)} style={removeBtnStyle} data-testid={`pref-custom-remove-${idx}`}>×</button>
        </div>
      ))}
      <button onClick={addCustomPref} style={{ ...addBtnStyle }} data-testid="pref-custom-add">+ Add Preference</button>
    </div>
  );
};

// ─── Tab: Custom Fields ───────────────────────────────────────────────
const CustomFieldsTab = ({ draft, setDraft, save }) => {
  const update = (idx, key, value) => {
    const next = [...(draft.custom_fields || [])];
    next[idx] = { ...next[idx], [key]: value };
    setDraft(d => ({ ...d, custom_fields: next }));
  };
  const commit = () => save({ custom_fields: draft.custom_fields });
  const add = () => setDraft(d => ({ ...d, custom_fields: [...(d.custom_fields || []), { key: '', value: '', type: 'text' }] }));
  const remove = (idx) => {
    const next = (draft.custom_fields || []).filter((_, i) => i !== idx);
    setDraft(d => ({ ...d, custom_fields: next }));
    save({ custom_fields: next });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-testid="custom-tab">
      <p style={{ fontSize: 11, color: T.light, margin: 0 }}>
        Add any field you want to track — text, number, date, or yes/no.
      </p>
      {(draft.custom_fields || []).map((cf, idx) => (
        <div key={idx} style={{ background: T.card, border: `1px solid ${T.border}`, padding: 10 }} data-testid={`custom-field-${idx}`}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <input
              value={cf.key || ''}
              onChange={e => update(idx, 'key', e.target.value)}
              onBlur={commit}
              placeholder="Field name"
              style={inputStyle({ flex: 1 })}
              data-testid={`cf-key-${idx}`}
            />
            <select
              value={cf.type || 'text'}
              onChange={e => { update(idx, 'type', e.target.value); }}
              onBlur={commit}
              style={{ ...inputStyle({ width: 90 }), color: T.white }}
              data-testid={`cf-type-${idx}`}
            >
              {CF_TYPES.map(t => <option key={t} value={t} style={{ background: T.dark }}>{t}</option>)}
            </select>
            <button onClick={() => remove(idx)} style={removeBtnStyle} data-testid={`cf-remove-${idx}`}>×</button>
          </div>
          {cf.type === 'boolean' ? (
            <button
              onClick={() => { update(idx, 'value', !cf.value); save({ custom_fields: (draft.custom_fields || []).map((x, i) => i === idx ? { ...x, value: !cf.value } : x) }); }}
              style={{
                width: '100%', background: cf.value ? T.green : T.dark, color: cf.value ? '#FFFFFF' : T.white,
                border: `1px solid ${T.border}`, padding: '8px', fontSize: 12, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer',
              }}
              data-testid={`cf-value-${idx}`}
            >{cf.value ? 'Yes' : 'No'}</button>
          ) : (
            <input
              type={cf.type === 'number' ? 'number' : cf.type === 'date' ? 'date' : 'text'}
              value={cf.value || ''}
              onChange={e => update(idx, 'value', e.target.value)}
              onBlur={commit}
              placeholder="Value"
              style={inputStyle({ width: '100%' })}
              data-testid={`cf-value-${idx}`}
            />
          )}
        </div>
      ))}
      <button onClick={add} style={addBtnStyle} data-testid="cf-add">+ Add Custom Field</button>
    </div>
  );
};

// ─── Tab: History ─────────────────────────────────────────────────────
const HistoryTab = ({ data }) => {
  const items = useMemo(() => {
    const out = [];
    (data.bookings || []).forEach(b => out.push({ type: 'Booking', date: b.event_date, label: b.vehicle || 'Vehicle TBD', amount: b.total }));
    (data.quotes || []).forEach(q => out.push({ type: 'Quote', date: q.created_at, label: q.service_type || 'Quote', amount: q.total || q.subtotal }));
    (data.invoices || []).forEach(i => out.push({ type: 'Invoice', date: i.created_at, label: i.invoice_id, amount: i.subtotal, status: i.status }));
    (data.contracts || []).forEach(c => out.push({ type: 'Contract', date: c.created_at, label: c.contract_type, amount: c.total_amount, status: c.status }));
    return out.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
  }, [data]);

  if (items.length === 0) return <p style={{ color: T.dim, fontSize: 12, textAlign: 'center', padding: 24 }} data-testid="history-empty">No history yet.</p>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="history-tab">
      {items.map((it, i) => (
        <div key={i} style={{ background: T.card, border: `1px solid ${T.border}`, padding: 10, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ fontSize: 10, color: T.red, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, margin: 0 }}>{it.type}</p>
            <p style={{ fontSize: 12, color: T.white, margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.label}</p>
            <p style={{ fontSize: 10, color: T.dim, margin: '2px 0 0' }}>{(it.date || '').slice(0, 10)}</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            {it.amount > 0 && <p style={{ fontSize: 12, color: T.gold, fontFamily: 'DM Mono, monospace', fontWeight: 700, margin: 0 }}>{fmt$(it.amount)}</p>}
            {it.status && <p style={{ fontSize: 9, color: T.light, margin: '2px 0 0', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{it.status}</p>}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Primitives ───────────────────────────────────────────────────────
const Stat = ({ label, value, color = T.white }) => (
  <div style={{ background: T.card, border: `1px solid ${T.border}`, padding: '10px 6px', textAlign: 'center' }}>
    <p style={{ fontSize: 8, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, margin: 0 }}>{label}</p>
    <p style={{ fontSize: 14, fontWeight: 900, color, margin: '3px 0 0', fontFamily: 'DM Mono, monospace', fontVariantNumeric: 'tabular-nums' }}>{value}</p>
  </div>
);

const FieldRow = ({ label, children }) => (
  <div>
    <label style={{ fontSize: 9, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.2em', fontWeight: 700, display: 'block', marginBottom: 4 }}>{label}</label>
    {children}
  </div>
);

const inputStyle = (extra = {}) => ({
  background: T.dark, border: `1px solid ${T.border}`, color: T.white,
  padding: '10px 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box',
  fontFamily: 'inherit',
  ...extra,
});

const removeBtnStyle = {
  background: T.dark, border: `1px solid ${T.border}`, color: T.red,
  padding: '0 10px', cursor: 'pointer', fontSize: 16, fontWeight: 700,
};

const addBtnStyle = {
  background: 'transparent', border: `1px dashed ${T.border}`, color: T.red,
  padding: '10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.1em', cursor: 'pointer',
};

const EditableText = ({ value, onChange, onCommit, placeholder, type = 'text', style = {}, testid }) => (
  <input
    type={type}
    value={value ?? ''}
    onChange={e => onChange?.(e.target.value)}
    onBlur={e => onCommit?.(e.target.value)}
    onKeyDown={e => { if (e.key === 'Enter' && type !== 'date') e.currentTarget.blur(); }}
    placeholder={placeholder}
    style={inputStyle({ width: '100%', ...style })}
    data-testid={testid}
  />
);

const EditableTextarea = ({ value, onChange, onCommit, placeholder, rows = 3, testid }) => (
  <textarea
    value={value ?? ''}
    onChange={e => onChange?.(e.target.value)}
    onBlur={e => onCommit?.(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    style={inputStyle({ width: '100%', resize: 'vertical' })}
    data-testid={testid}
  />
);

const SelectBox = ({ value, onChange, options, testid }) => (
  <select
    value={value || ''}
    onChange={e => onChange?.(e.target.value)}
    style={inputStyle({ width: '100%', color: T.white })}
    data-testid={testid}
  >
    {options.map(o => <option key={o} value={o} style={{ background: T.dark }}>{o || '— none —'}</option>)}
  </select>
);

const TagEditor = ({ tags, onChange, testid }) => {
  const [input, setInput] = useState('');
  const add = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    const t = input.trim();
    if (!t) return;
    if (tags.includes(t)) { setInput(''); return; }
    onChange([...tags, t]);
    setInput('');
  };
  const remove = (t) => onChange(tags.filter(x => x !== t));
  return (
    <div data-testid={testid}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
        {tags.map(t => (
          <span key={t} style={{ background: T.red, color: '#FFFFFF', padding: '4px 8px', fontSize: 10, fontWeight: 700, display: 'flex', gap: 4, alignItems: 'center' }} data-testid={`tag-${t}`}>
            {t}
            <button type="button" onClick={() => remove(t)} style={{ background: 'none', border: 0, color: '#FFFFFF', cursor: 'pointer', padding: 0, fontSize: 12, lineHeight: 1 }} data-testid={`tag-remove-${t}`}>×</button>
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Add tag…"
          style={inputStyle({ flex: 1 })}
          data-testid="tag-input"
        />
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={add}
          style={{ background: T.red, color: '#FFFFFF', border: 0, padding: '0 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', cursor: 'pointer' }}
          data-testid="tag-add"
        >Add</button>
      </div>
    </div>
  );
};
